import AutonomousSchedulerService from './AutonomousSchedulerService';

const SETTINGS_KEY = 'infinity-memoburst-practice-settings';

const DEFAULT_SETTINGS = {
  practiceCardTypeFilter: 'all',
  maxExcludeGrade: 5,
  excludedGrades: [5],
  maxScore: 500,
};

const VALID_GRADES = [1, 2, 3, 4, 5];
const MAX_CARD_LEVEL = 8;
const LEVEL_UP_THRESHOLDS = {
  simple: 150,
  normal: 400,
  reading: 60,
};
const LEARNING_CURVE_MAX_DAYS = 15;
const GRADE_THREE_RETEST_DAYS = 3;
const PRACTICE_PHASES = {
  MEMORY_TRIAL: 'memoryTrial',
  CORE_TRAINING: 'coreTraining',
};
const CARD_TYPES = {
  SIMPLE: 'simple',
  NORMAL: 'normal',
  READING: 'reading',
  DIALOG: 'dialog',
};
const PRACTICE_CARD_TYPE_FILTERS = {
  ALL: 'all',
  WORDS: 'words',
  SENTENCES: 'sentences',
  ARTICLES: 'articles',
};
const GRADUATION_TYPES = {
  MANUAL: 'manual',
  SCORE: 'score',
  EARLY: 'early',
};
const SCORE_LIMIT_BY_CARD_TYPE = {
  [CARD_TYPES.SIMPLE]: 200,
  [CARD_TYPES.NORMAL]: 500,
  [CARD_TYPES.READING]: 72,
  [CARD_TYPES.DIALOG]: 72,
};
const READING_GRADE_THRESHOLDS = [
  { repeats: 72, grade: 5 },
  { repeats: 64, grade: 4 },
  { repeats: 48, grade: 3 },
  { repeats: 24, grade: 2 },
  { repeats: 0, grade: 1 },
];
const NORMAL_EXPOSURE_LEVEL_THRESHOLDS = [
  { score: 500, level: 5 },
  { score: 350, level: 4 },
  { score: 200, level: 3 },
  { score: 100, level: 2 },
  { score: 0, level: 1 },
];
const SIMPLE_EXPOSURE_LEVEL_RATIOS = [
  { ratio: 1, level: 5 },
  { ratio: 0.7, level: 4 },
  { ratio: 0.4, level: 3 },
  { ratio: 0.2, level: 2 },
  { ratio: 0, level: 1 },
];
const EARLY_GRADUATION_TEST_INDEX_BY_CARD_TYPE = {
  [CARD_TYPES.SIMPLE]: 5,
  [CARD_TYPES.NORMAL]: 5,
};

function normalizeMaxExcludeGrade(value, fallback = DEFAULT_SETTINGS.maxExcludeGrade) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }

  const grade = Math.floor(numeric);
  return grade >= 0 && grade <= 5 ? grade : fallback;
}

function getExcludedGradesFromMax(maxExcludeGrade = DEFAULT_SETTINGS.maxExcludeGrade) {
  const threshold = normalizeMaxExcludeGrade(maxExcludeGrade);
  if (threshold === 0) return [];

  return VALID_GRADES.filter((grade) => grade >= threshold);
}

function getMaxExcludeGradeFromExcludedGrades(excludedGrades = DEFAULT_SETTINGS.excludedGrades) {
  const grades = excludedGrades instanceof Set
    ? excludedGrades
    : new Set(Array.isArray(excludedGrades) ? excludedGrades : []);

  if (!grades.has(5)) return 0;

  let threshold = 5;
  for (let grade = 4; grade >= 1; grade -= 1) {
    if (!grades.has(grade)) break;
    threshold = grade;
  }

  return threshold;
}

function normalizeMaxScore(value, fallback = DEFAULT_SETTINGS.maxScore) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return fallback;
  }

  return Math.floor(numeric);
}

function getCardPracticeCount(card) {
  if (card?.isReadingGroup && Array.isArray(card.readingChildren) && card.readingChildren.length > 0) {
    return Math.round(
      card.readingChildren.reduce((total, child) => {
        const count = Number(child?.practiceCount ?? child?.repeatCount ?? 0);
        return total + (Number.isFinite(count) && count > 0 ? count : 0);
      }, 0) / card.readingChildren.length
    );
  }

  const count = Number(card?.practiceCount ?? card?.repeatCount ?? 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function getReadingAutoGrade(practiceCount) {
  const count = Math.max(0, Number(practiceCount) || 0);
  return READING_GRADE_THRESHOLDS.find((item) => count >= item.repeats)?.grade || 1;
}

function getCardScore(card) {
  if (card?.isReadingGroup && Array.isArray(card.readingChildren) && card.readingChildren.length > 0) {
    return Math.round(
      card.readingChildren.reduce((total, child) => {
        const score = Number(child?.score ?? 0);
        return total + (Number.isFinite(score) && score > 0 ? score : 0);
      }, 0) / card.readingChildren.length
    );
  }

  const score = Number(card?.score ?? 0);
  return Number.isFinite(score) && score > 0 ? score : 0;
}

function normalizeGraduationType(value) {
  return Object.values(GRADUATION_TYPES).includes(value) ? value : null;
}

function getCardTestIndex(card) {
  const testIndex = Number(card?.testIndex ?? 0);
  return Number.isInteger(testIndex) && testIndex > 0 ? testIndex : 0;
}

function normalizeCardLevel(value) {
  const level = Number(value);
  return Number.isInteger(level) && level >= 1 && level <= MAX_CARD_LEVEL ? level : 1;
}

function getCardLevel(card) {
  return normalizeCardLevel(card?.level);
}

function getCardLevelBadgeLabel(card) {
  const level = getCardLevel(card);
  if (level === 1) return '';
  return `Lv. ${level}`;
}

function getCardType(card) {
  if (card?.cardType === CARD_TYPES.DIALOG) return CARD_TYPES.READING;
  if (card?.cardType === CARD_TYPES.READING) return CARD_TYPES.READING;
  return card?.cardType === CARD_TYPES.SIMPLE ? CARD_TYPES.SIMPLE : CARD_TYPES.NORMAL;
}

function getCardTypeLabel(card) {
  const cardType = getCardType(card);
  if (cardType === CARD_TYPES.READING) return 'Reading';
  return cardType === CARD_TYPES.SIMPLE ? 'Short' : 'Sentence';
}

function normalizePracticeCardTypeFilter(value) {
  return Object.values(PRACTICE_CARD_TYPE_FILTERS).includes(value)
    ? value
    : PRACTICE_CARD_TYPE_FILTERS.ALL;
}

function matchesPracticeCardTypeFilter(card, filter = PRACTICE_CARD_TYPE_FILTERS.ALL) {
  const normalizedFilter = normalizePracticeCardTypeFilter(filter);
  if (normalizedFilter === PRACTICE_CARD_TYPE_FILTERS.ALL) return true;

  const cardType = getCardType(card);
  if (normalizedFilter === PRACTICE_CARD_TYPE_FILTERS.WORDS) {
    return cardType === CARD_TYPES.SIMPLE;
  }
  if (normalizedFilter === PRACTICE_CARD_TYPE_FILTERS.SENTENCES) {
    return cardType === CARD_TYPES.NORMAL;
  }
  if (normalizedFilter === PRACTICE_CARD_TYPE_FILTERS.ARTICLES) {
    return cardType === CARD_TYPES.READING;
  }

  return true;
}

function getEarlyGraduationThreshold(card) {
  return EARLY_GRADUATION_TEST_INDEX_BY_CARD_TYPE[getCardType(card)] ||
    EARLY_GRADUATION_TEST_INDEX_BY_CARD_TYPE[CARD_TYPES.NORMAL];
}

function getCardScoreLimit(card) {
  const cardType = getCardType(card);
  if (cardType === CARD_TYPES.READING && card?.levelUpMethod !== 'review') return 72;

  const scoreLimit = Number(card?.scoreLimit);
  if (Number.isInteger(scoreLimit) && scoreLimit > 0) {
    return scoreLimit;
  }

  return SCORE_LIMIT_BY_CARD_TYPE[cardType] || SCORE_LIMIT_BY_CARD_TYPE[CARD_TYPES.NORMAL];
}

function getLevelBonusPoints(card) {
  return 0;
}

function isUnderMaxScore(card, maxScore = null) {
  if (isGraduated(card)) return false;

  const cardType = getCardType(card);
  if (cardType === CARD_TYPES.READING || cardType === CARD_TYPES.DIALOG) {
    return getCardPracticeCount(card) < getCardScoreLimit(card);
  }

  const cardLimit = getCardScoreLimit(card);
  const scoreLimit = maxScore === null || maxScore === undefined
    ? cardLimit
    : Math.min(cardLimit, normalizeMaxScore(maxScore));

  return getCardScore(card) < scoreLimit;
}

function isCardCompleted(card, scoreGoal = null) {
  if (card?.isReadingGroup && Array.isArray(card.readingChildren)) return isGraduated(card);
  if (isGraduated(card)) return true;

  const cardType = getCardType(card);
  const resolvedScoreGoal = scoreGoal === null || scoreGoal === undefined
    ? getCardScoreLimit(card)
    : normalizeMaxScore(scoreGoal);

  return cardType === CARD_TYPES.READING
    ? getCardPracticeCount(card) >= resolvedScoreGoal
    : getCardScore(card) >= resolvedScoreGoal;
}

function normalizeGrade(value) {
  const grade = Number(value);
  return Number.isFinite(grade) && grade >= 1 && grade <= 5 ? grade : 0;
}

function getLastGrade(card) {
  if (!card) return 0;
  return normalizeGrade(card.assessmentGrade);
}

function getAssessmentGrade(card) {
  if (!card) return 0;

  if (getCardType(card) === CARD_TYPES.READING) return 0;

  return normalizeGrade(card.assessmentGrade);
}

function getExposureLevel(card) {
  const cardType = getCardType(card);
  if (cardType === CARD_TYPES.READING) {
    return getReadingAutoGrade(getCardPracticeCount(card) * 72 / getCardScoreLimit(card));
  }

  const score = getCardScore(card);
  if (cardType === CARD_TYPES.SIMPLE) {
    const scoreLimit = getCardScoreLimit(card);
    const ratio = scoreLimit > 0 ? Math.min(1, score / scoreLimit) : 0;
    return SIMPLE_EXPOSURE_LEVEL_RATIOS.find((item) => ratio >= item.ratio)?.level || 1;
  }

  return NORMAL_EXPOSURE_LEVEL_THRESHOLDS.find((item) => score >= item.score)?.level || 1;
}

function getPracticeIntensityLevel(card) {
  return Math.max(getAssessmentGrade(card) || 1, getExposureLevel(card));
}

function getListeningProgress(card) {
  const cardType = getCardType(card);
  const progressValue = cardType === CARD_TYPES.READING
    ? getCardPracticeCount(card)
    : getCardScore(card);
  const limit = getCardScoreLimit(card);
  return limit > 0 ? Math.min(1, Math.max(0, progressValue / limit)) : 0;
}

function isGraduated(card) {
  if (!card) return false;
  if (card.isReadingGroup && Array.isArray(card.readingChildren)) {
    const children = card.readingChildren.filter((child) => !child.retiredAt);
    return children.length > 0 && children.every((child) => isGraduated({ ...child, cardType: 'reading' }));
  }
  if (normalizeGraduationType(card.graduationType) || card.completedAt) return true;
  if (getAssessmentGrade(card) === 5) return true;

  const cardType = getCardType(card);
  return cardType === CARD_TYPES.READING
    ? getCardPracticeCount(card) >= getCardScoreLimit(card)
    : getCardScore(card) >= getCardScoreLimit(card);
}

function getPracticeCardContent(card) {
  return {
    question: String(card?.front || ''),
    answer: String(card?.back || ''),
    usesLevelUpContent: false,
  };
}

function getPracticeDisplayCard(card) {
  if (!card) return card;
  const content = getPracticeCardContent(card);
  if (!content.usesLevelUpContent) return card;

  return {
    ...card,
    front: content.question,
    back: content.answer,
    memo: null,
    contextPre: null,
    contextAfter: null,
    backMemo: null,
  };
}

function isAssessmentGradeFour(card) {
  return getCardType(card) !== CARD_TYPES.READING && getAssessmentGrade(card) === 4;
}

function getDaysUntilNextPractice(card) {
  const grade = getAssessmentGrade(card);
  if (grade === 3) return GRADE_THREE_RETEST_DAYS;
  if (grade !== 4) return 0;
  return Math.min(getCardTestIndex(card) * 3 + 2, LEARNING_CURVE_MAX_DAYS);
}

function calculateNextTestIndex(card, nextGrade) {
  const grade = normalizeGrade(nextGrade);
  if (grade !== 4) return 0;
  return getAssessmentGrade(card) === 4 ? getCardTestIndex(card) + 1 : 0;
}

function shouldEarlyGraduateAfterTest(card, nextTestIndex) {
  return getAssessmentGrade(card) === 4 &&
    Number.isInteger(nextTestIndex) &&
    nextTestIndex >= getEarlyGraduationThreshold(card);
}

function resolveGraduationType(card, nextGrade, requestedType = null) {
  const existingType = normalizeGraduationType(card?.graduationType);
  if (existingType) return existingType;

  const grade = normalizeGrade(nextGrade);
  if (grade !== 5) return null;

  return normalizeGraduationType(requestedType) || GRADUATION_TYPES.MANUAL;
}

function getCardGraduationType(card) {
  const storedType = normalizeGraduationType(card?.graduationType);
  if (storedType) return storedType;

  return getAssessmentGrade(card) === 5 ? GRADUATION_TYPES.MANUAL : null;
}

function isGradeExcluded(grade, excludedGrades = []) {
  if (excludedGrades instanceof Set) {
    return excludedGrades.has(grade);
  }

  return Array.isArray(excludedGrades) && excludedGrades.includes(grade);
}

function isCardUpgradeable(card, { allowGraduated = false, readingGroup = null } = {}) {
  if (!card || card.levelUpUsed || card.isReadingGroup) return false;
  if (allowGraduated && isGraduated(card)) return true;
  const cardType = getCardType(card);
  if (cardType === CARD_TYPES.READING && readingGroup) {
    const activeChildren = (readingGroup.readingChildren || []).filter((child) => !child.retiredAt);
    return activeChildren.length > 0 && getCardPracticeCount({
      ...readingGroup, isReadingGroup: true, readingChildren: activeChildren,
    }) >= LEVEL_UP_THRESHOLDS.reading;
  }
  const threshold = LEVEL_UP_THRESHOLDS[cardType];
  const progress = cardType === CARD_TYPES.READING
    ? getCardPracticeCount(card)
    : getCardScore(card);
  return Number.isFinite(threshold) && progress > threshold;
}

function applyFilters(flashcards = [], filters = {}) {
  const practiceCardTypeFilter = normalizePracticeCardTypeFilter(filters.practiceCardTypeFilter);
  const excludedGrades = filters.maxExcludeGrade !== undefined
    ? getExcludedGradesFromMax(filters.maxExcludeGrade)
    : (filters.excludedGrades || []);
  const hasExplicitMaxScore =
    filters.maxScore !== undefined ||
    filters.maxRepeatCount !== undefined ||
    filters.practiceCountThreshold !== undefined;
  const maxScore = hasExplicitMaxScore
    ? normalizeMaxScore(filters.maxScore ?? filters.maxRepeatCount ?? filters.practiceCountThreshold)
    : null;

  const matchesFilters = (card) => {
    if (!matchesPracticeCardTypeFilter(card, practiceCardTypeFilter)) {
      return false;
    }

    if (isGradeExcluded(getAssessmentGrade(card), excludedGrades)) {
      return false;
    }

    if (isLearningCurveHidden(card)) {
      return false;
    }

    if (isPracticedToday(card)) {
      return false;
    }

    return isUnderMaxScore(card, maxScore);
  };

  return (flashcards || []).reduce((result, card) => {
    if (card?.isReadingGroup && Array.isArray(card.readingChildren)) {
      if (!matchesPracticeCardTypeFilter(card, practiceCardTypeFilter)) return result;
      const readingChildren = card.readingChildren.map((child) => ({
        ...child,
        practiceAvailable: child.practiceAvailable !== false && matchesFilters(child),
      }));
      if (!readingChildren.some((child) => child.practiceAvailable)) return result;
      result.push({ ...card, readingChildren });
      return result;
    }

    if (matchesFilters(card)) result.push(card);
    return result;
  }, []);
}

function calculateFilteredCount(flashcards = [], filters = {}) {
  return applyFilters(flashcards, filters).length;
}

function getCardId(card) {
  return card?.id ?? card?._id;
}

function getLastPracticeTimestamp(card) {
  return card?.lastPracticedAt ?? null;
}

function getLocalDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isPracticedToday(card, now = new Date()) {
  const lastTimestamp = getLastPracticeTimestamp(card);
  if (!lastTimestamp) return false;

  const lastDateKey = getLocalDateKey(lastTimestamp);
  const todayKey = getLocalDateKey(now);
  return Boolean(lastDateKey && todayKey && lastDateKey === todayKey);
}

function getDaysSince(card, nowValue = new Date()) {
  const lastTimestamp = getLastPracticeTimestamp(card);

  if (!lastTimestamp) return 0;

  const last = new Date(lastTimestamp);
  if (Number.isNaN(last.getTime())) return 0;

  const now = nowValue instanceof Date ? new Date(nowValue) : new Date(nowValue);
  if (Number.isNaN(now.getTime())) return 0;
  last.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  return Math.max(0, Math.floor((now - last) / 86400000));
}

function getAgeScore(card) {
  return AutonomousSchedulerService.getAgeScore(
    getLastPracticeTimestamp(card) ? getDaysSince(card) : null
  );
}

function getAgePriorityMultiplier(card) {
  return getAgeScore(card);
}

function isLearningCurveHidden(card, now = new Date()) {
  if (getCardType(card) === CARD_TYPES.READING || getCardType(card) === CARD_TYPES.DIALOG) return false;
  const grade = getAssessmentGrade(card);
  if (grade !== 3 && grade !== 4) return false;
  if (!getLastPracticeTimestamp(card)) return false;
  return getDaysSince(card, now) < getDaysUntilNextPractice(card);
}

function isLearningCurveDue(card, now = new Date()) {
  const grade = getAssessmentGrade(card);
  if (grade !== 3 && grade !== 4) return false;
  return !isLearningCurveHidden(card, now);
}

function getPracticePhase(card, now = new Date()) {
  if (Object.values(PRACTICE_PHASES).includes(card?.practicePhase)) {
    return card.practicePhase;
  }

  return isLearningCurveDue(card, now)
    ? PRACTICE_PHASES.MEMORY_TRIAL
    : PRACTICE_PHASES.CORE_TRAINING;
}

function createPracticePhaseQueue(cards = [], now = new Date()) {
  const phaseCards = (cards || [])
    .filter((card) => !isLearningCurveHidden(card, now))
    .map((card) => ({
      ...card,
      practicePhase: getPracticePhase(card, now),
    }));
  const memoryTrialCards = phaseCards.filter(
    (card) => card.practicePhase === PRACTICE_PHASES.MEMORY_TRIAL
  );
  const coreTrainingCards = phaseCards.filter(
    (card) => card.practicePhase === PRACTICE_PHASES.CORE_TRAINING
  );

  return [
    ...AutonomousSchedulerService.createPracticeQueue(memoryTrialCards, null, now, { randomOrder: true }),
    ...AutonomousSchedulerService.createPracticeQueue(coreTrainingCards, null, now),
  ];
}

function calculatePriorityScore(card) {
  return AutonomousSchedulerService.scoreCard(card);
}

function selectNextBatch(flashcards = [], filters = {}, previousBatch = [], batchSize = 30) {
  const filteredCards = applyFilters(flashcards, filters);
  if (!filteredCards.length) return [];

  const previousIds = new Set((previousBatch || []).map(getCardId).filter(Boolean));
  // Exclude earlier cards before scheduling so removing them cannot undo diversity.
  const freshCards = filteredCards.filter((card) => !previousIds.has(getCardId(card)));
  const orderedCards = createPracticePhaseQueue(freshCards);

  return orderedCards.slice(0, batchSize);
}

function loadSettings() {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const maxExcludeGrade = normalizeMaxExcludeGrade(
        parsed.maxExcludeGrade,
        getMaxExcludeGradeFromExcludedGrades(parsed.excludedGrades)
      );

      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        reverseMode: false,
        practiceCardTypeFilter: normalizePracticeCardTypeFilter(parsed.practiceCardTypeFilter),
        maxExcludeGrade,
        excludedGrades: getExcludedGradesFromMax(maxExcludeGrade),
        maxScore: normalizeMaxScore(parsed.maxScore ?? parsed.maxRepeatCount),
      };
    }
  } catch (error) {
    console.error('Failed to load practice settings:', error);
  }
  return { ...DEFAULT_SETTINGS, reverseMode: false };
}

function saveSettings(settings) {
  try {
    const maxExcludeGrade = normalizeMaxExcludeGrade(settings?.maxExcludeGrade);
    const currentSettings = { ...(settings || {}) };
    delete currentSettings.reverseMode;
    delete currentSettings.weights;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({
      ...currentSettings,
      practiceCardTypeFilter: normalizePracticeCardTypeFilter(settings?.practiceCardTypeFilter),
      maxExcludeGrade,
      excludedGrades: getExcludedGradesFromMax(maxExcludeGrade),
      maxScore: normalizeMaxScore(settings?.maxScore ?? settings?.maxRepeatCount),
    }));
  } catch (error) {
    console.error('Failed to save practice settings:', error);
  }
}

const PracticeSettingsService = {
  loadSettings,
  saveSettings,
  normalizeMaxExcludeGrade,
  getExcludedGradesFromMax,
  getMaxExcludeGradeFromExcludedGrades,
  normalizeMaxScore,
  getCardPracticeCount,
  getReadingAutoGrade,
  getCardScore,
  normalizeGraduationType,
  getCardGraduationType,
  getCardTestIndex,
  normalizeCardLevel,
  getCardLevel,
  getCardLevelBadgeLabel,
  getCardType,
  getCardTypeLabel,
  normalizePracticeCardTypeFilter,
  matchesPracticeCardTypeFilter,
  getEarlyGraduationThreshold,
  getCardScoreLimit,
  getLastPracticeTimestamp,
  isPracticedToday,
  getAgeScore,
  getAgePriorityMultiplier,
  getDaysUntilNextPractice,
  calculateNextTestIndex,
  shouldEarlyGraduateAfterTest,
  resolveGraduationType,
  getLevelBonusPoints,
  getLastGrade,
  getAssessmentGrade,
  getExposureLevel,
  getPracticeIntensityLevel,
  getListeningProgress,
  getPracticeCardContent,
  getPracticeDisplayCard,
  isGraduated,
  isAssessmentGradeFour,
  isCardCompleted,
  isCardUpgradeable,
  isUnderMaxScore,
  isLearningCurveHidden,
  isLearningCurveDue,
  getPracticePhase,
  createPracticePhaseQueue,
  applyFilters,
  calculateFilteredCount,
  calculatePriorityScore,
  selectNextBatch,
  CARD_TYPES,
  PRACTICE_CARD_TYPE_FILTERS,
  GRADUATION_TYPES,
  SCORE_LIMIT_BY_CARD_TYPE,
  EARLY_GRADUATION_TEST_INDEX_BY_CARD_TYPE,
  LEVEL_UP_THRESHOLDS,
  LEARNING_CURVE_MAX_DAYS,
  GRADE_THREE_RETEST_DAYS,
  PRACTICE_PHASES,
  READING_GRADE_THRESHOLDS,
  NORMAL_EXPOSURE_LEVEL_THRESHOLDS,
  SIMPLE_EXPOSURE_LEVEL_RATIOS,
  DEFAULT_SETTINGS,
};

export default PracticeSettingsService;
